import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import { exchangeSsoAssertion, getCurrentUser } from '../api/authApi'
import { ApiError } from '../api/client'
import { AuthContext, type AuthContextValue } from './AuthContext'
import { AUTH_CALLBACK_PATH } from './config'
import {
  AUTH_SESSION_INVALID_EVENT,
  clearAccessToken,
  readAccessToken,
  writeAccessToken,
} from './storage'
import {
  isAuthenticatedSsoUser,
  type AuthSession,
  type AuthStatus,
} from './types'

const SESSION_UNAVAILABLE_MESSAGE =
  '로그인 상태를 확인하지 못했습니다. 잠시 후 다시 시도해 주세요.'

function isAuthCallbackLocation(): boolean {
  if (typeof window === 'undefined') return false
  return window.location.pathname.replace(/\/+$/, '') === AUTH_CALLBACK_PATH
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('checking')
  const [session, setSession] = useState<AuthSession | null>(null)
  const [authError, setAuthError] = useState<string | null>(null)
  const operationIdRef = useRef(0)
  const bootstrappedRef = useRef(false)

  const setGuest = useCallback(() => {
    setSession(null)
    setStatus('guest')
    setAuthError(null)
  }, [])

  const logout = useCallback(() => {
    operationIdRef.current += 1
    clearAccessToken()
    setGuest()
  }, [setGuest])

  const refreshSession = useCallback(async () => {
    const accessToken = readAccessToken()
    const operationId = ++operationIdRef.current

    if (!accessToken) {
      setGuest()
      return
    }

    setStatus('checking')
    setAuthError(null)

    try {
      const user = await getCurrentUser()
      if (operationId !== operationIdRef.current) return

      if (!isAuthenticatedSsoUser(user)) {
        clearAccessToken()
        setGuest()
        return
      }

      setSession({
        accessToken,
        tokenType: 'Bearer',
        expiresIn: 0,
        user,
      })
      setStatus('authenticated')
    } catch (error: unknown) {
      if (operationId !== operationIdRef.current) return

      setSession(null)
      if (error instanceof ApiError && error.status === 401) {
        clearAccessToken()
        setGuest()
        return
      }

      setStatus('unavailable')
      setAuthError(SESSION_UNAVAILABLE_MESSAGE)
    }
  }, [setGuest])

  const completeSso = useCallback(async (assertion: string) => {
    const operationId = ++operationIdRef.current
    setStatus('checking')
    setAuthError(null)

    try {
      const nextSession = await exchangeSsoAssertion(assertion)
      if (
        !nextSession.accessToken?.trim() ||
        !isAuthenticatedSsoUser(nextSession.user)
      ) {
        throw new Error('SSO 응답에 로그인 정보가 없습니다.')
      }
      if (operationId !== operationIdRef.current) return nextSession.user

      writeAccessToken(nextSession.accessToken)
      setSession(nextSession)
      setStatus('authenticated')
      return nextSession.user
    } catch (error: unknown) {
      if (operationId === operationIdRef.current) {
        clearAccessToken()
        setGuest()
      }
      throw error
    }
  }, [setGuest])

  useEffect(() => {
    const handleInvalidSession = () => {
      operationIdRef.current += 1
      clearAccessToken()
      setGuest()
    }

    window.addEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession)
    return () => window.removeEventListener(AUTH_SESSION_INVALID_EVENT, handleInvalidSession)
  }, [setGuest])

  useEffect(() => {
    // The callback owns authentication while exchanging the staged assertion.
    if (isAuthCallbackLocation() || bootstrappedRef.current) return
    bootstrappedRef.current = true
    void refreshSession()
  }, [refreshSession])

  const value = useMemo<AuthContextValue>(() => ({
    status,
    session,
    user: session?.user ?? null,
    isAuthenticated: status === 'authenticated' && !!session?.user.loggedIn,
    authError,
    hasPermission: (permission: string) =>
      status === 'authenticated' && !!session?.user.permissions.includes(permission),
    completeSso,
    logout,
    refreshSession,
  }), [authError, completeSso, logout, refreshSession, session, status])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export default AuthProvider
